import { useQueries, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formsEdgeHeaders, formsEdgeUrl } from '@/lib/formsEdge';

export type FormsQuestionOption = {
  value: string;
  label: string;
  label_da?: string;
  score?: number;
};

export type FormsQuestion = {
  id: string;
  text: string;
  text_da?: string;
  type: string;
  category_id?: string;
  options?: FormsQuestionOption[];
  scaleMax?: number;
  scoring?: { method?: string; weight?: number; max_score?: number };
};

export type FormsScoringConfig = {
  categories?: Array<{
    category_id: string;
    name: string;
    color?: string;
    calculation_method?: 'average' | 'sum';
  }>;
};

export type FormsAssignmentDetail = {
  assignment_id: string;
  status: string;
  completed_at: string | null;
  created_at: string | null;
  admin_url: string;
  questionnaire: {
    name: string;
    description: string | null;
    description_da?: string | null;
    description_en?: string | null;
    questions: FormsQuestion[];
    scoring_config: FormsScoringConfig | null;
  } | null;
  respondent: {
    first_name: string;
    last_name: string;
  } | null;
  responses: Record<string, unknown>;
  score_details: unknown;
};

async function callAssignmentDetailEF(
  clinicId: string,
  formsAssignmentId: string,
): Promise<FormsAssignmentDetail> {
  const { data: session } = await supabase.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const resp = await fetch(
    formsEdgeUrl('forms-get-assignment-responses', {
      clinic_id: clinicId,
      forms_assignment_id: formsAssignmentId,
    }),
    {
      method: 'GET',
      headers: formsEdgeHeaders(token),
    },
  );

  const json: { ok: boolean; data?: FormsAssignmentDetail; error?: { message?: string } } =
    await resp.json();
  if (!json.ok || !json.data) {
    throw new Error(json.error?.message ?? 'forms-get-assignment-responses returned error');
  }
  return json.data;
}

export function loadFormsAssignmentDetail(
  clinicId: string,
  formsAssignmentId: string,
): Promise<FormsAssignmentDetail> {
  return callAssignmentDetailEF(clinicId, formsAssignmentId);
}

export function formsAssignmentDetailQueryKey(
  clinicId: string | null | undefined,
  formsAssignmentId: string | null | undefined,
) {
  return ['forms', 'assignment', 'detail', clinicId ?? null, formsAssignmentId ?? null] as const;
}

export function useFormsAssignmentDetail(
  clinicId: string | null | undefined,
  formsAssignmentId: string | null | undefined,
  enabled = true,
) {
  return useQuery<FormsAssignmentDetail, Error>({
    queryKey: formsAssignmentDetailQueryKey(clinicId, formsAssignmentId),
    enabled: Boolean(clinicId && formsAssignmentId && enabled),
    staleTime: 2 * 60 * 1000,
    queryFn: () => callAssignmentDetailEF(clinicId!, formsAssignmentId!),
  });
}

/** Same detail the answer panel loads, for several completed assignments. */
export function useFormsAssignmentDetails(
  clinicId: string | null | undefined,
  formsAssignmentIds: readonly string[],
) {
  return useQueries({
    queries: formsAssignmentIds.map((formsAssignmentId) => ({
      queryKey: formsAssignmentDetailQueryKey(clinicId, formsAssignmentId),
      enabled: Boolean(clinicId && formsAssignmentId),
      staleTime: 2 * 60 * 1000,
      queryFn: () => callAssignmentDetailEF(clinicId as string, formsAssignmentId),
    })),
  });
}
